import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  ILinkToolIdentityRequest,
  ILinkToolIdentityResponse,
  IOffboardingActionRequest,
  IOffboardingActionResponse,
  IOffboardingAuditLogEntry,
  IOffboardingAuditLogQuery,
  IOffboardingAuditLogResponse,
  IOffboardingEmployee,
  IOffboardingEmployeeResponse,
  IOffboardingEmployeeSearchResponse,
  IOffboardingListPage,
  IOffboardingListQuery,
  IOffboardingListResponse,
  IOffboardingOverview,
  IOffboardingOverviewResponse,
  IOffboardingToolAccess,
  IToolRemovalResponse,
  IToolRemovalResult,
} from '../interfaces/offboarding.interface';
import {
  OFFBOARDING_FINANCE_CONFIRMED_STORAGE_KEY_PREFIX,
  OFFBOARDING_SESSION_STORAGE_KEYS,
} from '../constants/offboarding.constants';
import * as Urls from '../urls/shared.urls';
import * as OffboardingUrls from '../urls/offboarding.urls';

@Injectable({ providedIn: 'root' })
export class OffboardingService {
  private readonly http = inject(HttpClient);

  lastSelectedEmployee: IOffboardingEmployee | null = null;

  getEmployee(userId: string): Observable<IOffboardingEmployee> {
    return this.http
      .get<IOffboardingEmployeeResponse>(`${Urls.base_url}${OffboardingUrls.offboardingEmployee(userId)}`)
      .pipe(map((response) => response.data));
  }

  searchEmployees(searchText: string): Observable<IOffboardingEmployee[]> {
    return this.http
      .get<IOffboardingEmployeeSearchResponse>(`${Urls.base_url}${OffboardingUrls.offboardingEmployeeSearch}`, {
        params: { searchText: searchText.trim() },
      })
      .pipe(map((response) => response.data ?? []));
  }

  getOverview(userId: string): Observable<IOffboardingOverview> {
    return this.http
      .get<IOffboardingOverviewResponse>(
        `${Urls.base_url}${OffboardingUrls.offboardingOverview(userId)}`,
      )
      .pipe(map((response) => response.data));
  }

  linkToolIdentity(
    userId: string,
    tool: string,
    handle: string,
  ): Observable<IOffboardingToolAccess> {
    const body: ILinkToolIdentityRequest = { handle };
    return this.http
      .post<ILinkToolIdentityResponse>(
        `${Urls.base_url}${OffboardingUrls.offboardingLinkIdentity(userId, tool)}`,
        body,
      )
      .pipe(map((response) => response.data));
  }

  performAction(userId: string, action: IOffboardingActionRequest): Observable<string> {
    return this.http
      .post<IOffboardingActionResponse>(`${Urls.base_url}${OffboardingUrls.offboardingActions(userId)}`, action)
      .pipe(map((response) => response.data?.message));
  }

  /** Declines the employee's open requests for a tool, then revokes every live item. */
  removeAllToolAccess(userId: string, tool: string): Observable<IToolRemovalResult> {
    const action: IOffboardingActionRequest = { type: 'TOOL_REMOVE_ALL', tool };
    return this.http
      .post<IToolRemovalResponse>(`${Urls.base_url}${OffboardingUrls.offboardingActions(userId)}`, action)
      .pipe(map((response) => response.data));
  }

  getOffboardingList(query: IOffboardingListQuery): Observable<IOffboardingListPage> {
    return this.http
      .get<IOffboardingListResponse>(`${Urls.base_url}${OffboardingUrls.offboardingEmployees}`, { params: this.toParams(query) })
      .pipe(
        map((response) => ({
          rows: response.data?.rows ?? [],
          count: response.data?.count ?? 0,
          statusCounts: response.data?.statusCounts ?? {},
        })),
      );
  }

  getAuditLogs(
    query: IOffboardingAuditLogQuery,
  ): Observable<{ rows: IOffboardingAuditLogEntry[]; count: number; categoryCounts: Record<string, number> }> {
    const params = this.toParams(query);
    return this.http
      .get<IOffboardingAuditLogResponse>(`${Urls.base_url}${OffboardingUrls.offboardingAuditLogs}`, { params })
      .pipe(
        map((response) => ({
          count: response.data?.count ?? 0,
          categoryCounts: response.data?.categoryCounts ?? {},
          rows: (response.data?.rows ?? []).map((entry) => ({
            ...entry,
            performedByName: entry.performer
              ? `${entry.performer.firstName ?? ''} ${entry.performer.lastName ?? ''}`.trim()
              : entry.performedByName,
          })),
        })),
      );
  }

  clearSession(): void {
    this.lastSelectedEmployee = null;
    try {
      const financeKeys = Object.keys(sessionStorage).filter((key) => key.startsWith(OFFBOARDING_FINANCE_CONFIRMED_STORAGE_KEY_PREFIX));
      [...OFFBOARDING_SESSION_STORAGE_KEYS, ...financeKeys].forEach((key) => sessionStorage.removeItem(key));
    } catch {
      return;
    }
  }

  private toParams(query: object): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    }
    return params;
  }
}
